import type {BulkUpdateEntityType} from 'types';
import type {LinkedLiterary} from 'schemaDefinition/types';
import type {
  ManifestationV4,
  OtherTitle,
  WorkMetadata,
  WorkV4,
} from 'services/metadata/types';
import type {HttpError} from './http/types';
import type {Schemas} from './dto.generated';

export type Tokens = {
  accessToken: string;
  idToken: string;
};

export type GetTokens = () => Promise<Tokens>;

export type LoadStatus = 'NotLoaded' | 'Loading' | 'Loaded' | 'Failed';

export type DataLoadStatus<TData> =
  | {
      status: 'NotLoaded' | 'Loading';
      partial?: boolean;
      data?: TData;
      error?: HttpError;
    }
  | {
      status: 'Loaded';
      partial?: boolean;
      data: TData;
      error?: HttpError;
    }
  | {
      status: 'Failed';
      partial?: boolean;
      data?: TData;
      error: HttpError;
    };

export type DataLoadByIdStatus<TData, TId extends string | number> = {
  [key in TId]?: DataLoadStatus<TData>;
};

//=== Domain types =================================================================================
// I.e. domain types used in front-end, based on DTOs from the API.

/**
 * @deprecated Use ProductStatus instead
 */
export type AvailabilityStatus = Required<
  Required<Schemas.MetadataDto>
>['availabilityStatus'];

export type ProductStatus = Required<Required<Schemas.ProductDto>>['status'];

//----- Receive -----
export type RegisteredReceiveContentResponse =
  Schemas.PhysicalContentReceivedResponse;

//----- DateType -----------------------------------------------------------------------------------

export type DateType = Schemas.DateTypeDto & {
  bce?: boolean;
};

//----- Work ---------------------------------------------------------------------------------------

export type Dewey = Schemas.DeweyDto;

//----- WorkSummary --------------------------------------------------------------------------------

export type WorkSummary = WorkV4 & {
  // Using required to avoid undefined which causes type errors in typed schema
  mainTitles: Required<ManifestationV4>['mainTitles'] | null;
  themaLabels?: WorkV4['themes'];
  themaCodes?: WorkV4['themes'];
  otherTitleType36?: OtherTitle['value'][]; // Deltittel
  otherTitleType37?: OtherTitle['value'][]; // Tittelnummer
};

//----- ML Prediction
export type MLPrediction = Schemas.MLPredictionDto;

//----- Series -------------------------------------------------------------------------------------
export type RelationRoleDto =
  | Schemas.WorkToWorkRelationDto['role']
  | Schemas.AgentRelationDto['role']
  | Schemas.SeriesRelationDto['role']
  | Schemas.AgentCorporationRelationDto['role']
  | Schemas.AgentPersonRelationDto['role'];

export type RelationTargetTypeDto =
  | Schemas.WorkToWorkRelationDto['type']
  | Schemas.AgentRelationDto['type']
  | Schemas.SeriesRelationDto['type'];

// ----- Search ----------------------------------------------------------------
/**
 * WorkHit is currently just an alias for WorkMetadata, but WorkHit (search response) might
 * be extended in the future. Also since WorkHit is sourced from elastic and not API, it should
 * not be used in any functional manner (e.g. linking), only presentations.
 */
export type WorkHit = WorkMetadata & {
  id: string;
};

//----- Mass Update --------------------------------------------------------------------------------
export type BulkUpdateCreate = {
  type: BulkUpdateEntityType;
  bulkUpdateField: BulkUpdateFieldName;
  bulkUpdateAction: BulkUpdateAction;
  /** Used by remove and replace */
  currentValues?: string[] | LinkedLiterary[];
  /** Used by add and replace */
  newValues?: string[] | LinkedLiterary[];
  addToFirstPosition?: boolean;
};

export type BulkUpdateCreateResponse = {
  id: string;
};

export type BulkUpdateAction = 'ADD' | 'REMOVE' | 'REPLACE';

export type BulkUpdateFieldName =
  | Exclude<Schemas.BulkUpdateWorkField['field'], 'seriesRelations'>
  | 'links';

export type BulkUpdateStatus =
  | 'NEW'
  | Schemas.BulkUpdateStatusResponse['status'];

export type BulkUpdateItemStatus =
  | 'NEW'
  | 'UNPROCESSED'
  | 'SUCCEEDED'
  | 'FAILED';

export type BulkUpdate = BulkUpdateCreate &
  Pick<Schemas.BulkUpdateStatusResponse, 'items'> & {
    id: string;
    status: BulkUpdateStatus;
    created: string; // Formatted as string in mapping
    completed?: string; // Formatted as string in mapping
  };

export type BulkUpdateList = {
  pages: number;
  elements: number;
  items: BulkUpdateSummary[];
};

export type BulkUpdateSummary = BulkUpdateCreate &
  Schemas.BulkUpdateSummaryItems &
  Pick<
    Schemas.BulkUpdateSummary,
    'id' | 'status' | 'items' | 'created' | 'completed'
  >;

export type BulkUpdateFailedItem = Schemas.BulkUpdateFailedItem;
export type BulkUpdateErrorKey =
  | 'WORK_ENTITY_NOT_FOUND'
  | 'VALUE_NOT_PRESENT'
  | 'VALUE_ALREADY_PRESENT'
  | 'INVALID_THEMA'
  | 'SYSTEM_ERROR';
export const BulkUpdateErrorKeys: BulkUpdateErrorKey[] = [
  'WORK_ENTITY_NOT_FOUND',
  'VALUE_NOT_PRESENT',
  'VALUE_ALREADY_PRESENT',
  'INVALID_THEMA',
  'SYSTEM_ERROR',
];
